/**
 * The bank accounts on the books (`bancos.ts` in shared, 2026-10-10): which one a transfer went to
 * or came from, and its lines, a dollar account's carrying the dollars they moved.
 *
 * Every record that moves money by transfer names its account (`cuenta`). One that names none is
 * from before there were others, or from a phone that had not updated, and is the first córdoba
 * account's, «Bancos», where every transfer always went: so the log replays as it always has.
 */

import {
  centavos,
  cuentaAbierta,
  esCuentaEnDolares,
  usdToCordobas,
  type Centavos,
  type CuentaBanco,
  type FxRate,
  type Settings,
} from '@abastece/shared';
import { conDolares, credit, debit, type JournalLine } from '../ledger/journal.js';

/** The account a transfer named, or «Bancos». */
export const cuentaDeTransferencia = (cuenta: CuentaBanco | null | undefined): CuentaBanco => cuenta ?? 'BANCOS';

/** Why a record naming `cuenta` is refused, or null: an account never opened takes nothing. */
export function cuentaCerradaParaSiempre(config: Settings, cuenta: CuentaBanco | null | undefined): string | null {
  if (!cuenta || cuentaAbierta(config.cuentasBanco, cuenta)) return null;
  return 'Esa cuenta de banco no existe';
}

/**
 * What the books call an account in what they say (a refusal, an entry's line): the owner's name
 * for it, or what the first of each currency always was.
 */
export function nombreDeCuentaBanco(config: Settings, cuenta: CuentaBanco): string {
  const puesta = config.cuentasBanco.find((c) => c.cuenta === cuenta);
  if (puesta) return `«${puesta.nombre}»`;
  return esCuentaEnDolares(cuenta) ? 'la cuenta en dólares' : 'el banco';
}

/**
 * A line on a bank account. A dollar account's carries the dollars it moved, `dolares`, and
 * córdobas at what they came to: `cordobas`, worked out by whoever calls this, so an entry can
 * balance the rest against the same figure.
 */
export function lineaDeBanco(
  lado: 'debe' | 'haber',
  cuenta: CuentaBanco,
  cordobas: Centavos,
  dolares: number,
  memo?: string,
): JournalLine {
  const linea = lado === 'debe' ? debit(cuenta, cordobas, memo) : credit(cuenta, cordobas, memo);
  return esCuentaEnDolares(cuenta) ? conDolares(linea, dolares) : linea;
}

/** Dollars at the official rate: what a dollar account carries them at, as general cash does. */
export const alOficial = (dolares: number, tasa: FxRate): Centavos => usdToCordobas(centavos(dolares), tasa);
